import React, { useState, useEffect, useContext } from 'react';
import { DataGridPro } from '@mui/x-data-grid-pro';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { getShipmentStatus } from '../../api/dashboard';
import PropTypes from 'prop-types';
import { StoreContext } from '../../components/Store';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import Paper from '@mui/material/Paper';
import { dateFormatChange } from '../../utils/date';
import LoadingSpinner from '../../components/LoadingSpinner';
import SnackbarManager from './SnackbarManager';
import { isEORIUser } from '../../utils/roles';

export default function AcceptedShipment(props) {
  const store = useContext(StoreContext);
  const [shipmentData, setShipmentData] = useState((!isEORIUser() && store.shipmentData[0]) || []);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [shipmentFilterObj, setShipmentFilterObj] = useState({
    filter: {
      clientJobNumber: '',
      departureStartDate: '',
      departureEndDate: '',
      arrivalStartDate: '',
      arrivalEndDate: '',
      vehicleIdentity: '',
      lrnMRN: '',
      status: '',
    },
    amount: 50,
    skip: 0,
  });

  const { countForApi, isResponseCame, shipmentStatusAPICall, callAPIcallTimeout } = props;
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });

  useEffect(() => {
    setPaginationModel({ page: 0, pageSize: 50 });
  }, [props.paginationResetKey]);

  const apiCallForShipmentData = (shipmentFilterObj) => {
    getShipmentStatus(shipmentFilterObj)
      .then((data) => {
        store.shipmentData[1](data.data);
        store.shipmentTotalCount[1](data.totalItems);
        const filteredData = data.data.filter((row) => row?.headerId);
        console.warn(`Filtered out ${data.data.length - filteredData.length} rows due to missing headerId.`);
        setShipmentData(filteredData);
        if (data.totalItems !== undefined) {
          store.shipmentTotalCount[1](data.totalItems);
        }
        isResponseCame();
      })
      .catch((err) => {
        store.showError(err.message);
      })
      .finally(() => setLoading(false));
  };

  const fetchStatusAndData = async () => {
    try {
      await apiCallForShipmentData(store.shipmentStatusFilterData[0]);
      await shipmentStatusAPICall();
    } catch (error) {
      console.error('Error fetching status and data:', error);
    }
  };

  useEffect(() => {
    async function fetchData() {
      try {
        const initialFilter = {
          ...shipmentFilterObj,
          ...store.shipmentStatusFilterData[0],
          amount: paginationModel.pageSize,
          skip: paginationModel.page * paginationModel.pageSize,
        };
        apiCallForShipmentData(initialFilter);
      } catch (err) {
        console.log(err);
        setShipmentFilterObj({});
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    if (callAPIcallTimeout) {
      apiCallForShipmentData(JSON.parse(sessionStorage.getItem('filterObj')));
    }
  }, [callAPIcallTimeout]);

  useEffect(() => {
    if (countForApi > 0) {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
      sessionStorage.setItem('filterObj', JSON.stringify(store.shipmentStatusFilterData[0]));
      getShipmentStatus(store.shipmentStatusFilterData[0])
        .then((data) => {
          store.shipmentStatusFilter[1](data.data);
          store.shipmentData[1](data.data);
          store.shipmentTotalCount[1](data.totalItems);
          const filteredData = data.data.filter((row) => row?.headerId);
          console.warn(`Filtered out ${data.data.length - filteredData.length} rows due to missing headerId.`);
          setShipmentData(filteredData);
          isResponseCame();
        })
        .catch((err) => {
          store.showError(err.message);
        });
    }
  }, [countForApi]);

  const handlePaginationModelChange = (newModel) => {
    setPaginationModel(newModel);
    const newSkip = newModel.page * newModel.pageSize;
    const newAmount = newModel.pageSize;

    const updatedFilterObj = {
      ...shipmentFilterObj,
      ...store.shipmentStatusFilterData[0],
      amount: newAmount,
      skip: newSkip,
    };
    setShipmentFilterObj(updatedFilterObj);
    apiCallForShipmentData(updatedFilterObj);
  };

  const navigatetoShipmentView = (row) => {
    navigate('/ShipmentView', { state: { res: row.headerId, companyId: row.companyId } });
  };

  const handleClick = (dataClickedItem) => {
    if (Object.keys(dataClickedItem).length > 0 && dataClickedItem.constructor === Object) {
      //console.log(dataClickedItem);
    }
    if (!dataClickedItem || !dataClickedItem.id) {
      console.error('Clicked row has no valid id:', dataClickedItem);
    }
  };

  const isOps = isEORIUser();
  const columns = [
    {
      field: 'clientJobNumber',
      headerName: 'Your Reference',
      flex: 1,
      headerClassName: 'theme--header',
      editable: true,
    },
    {
      field: 'vehicleIdentity',
      headerName: 'Vehicle Identity',
      flex: 1,
      editable: true,
      headerClassName: 'theme--header',
    },
    {
      field: 'departureDate',
      headerName: 'Departure Date',
      type: 'number',
      flex: 1,
      editable: true,
      align: 'left',
      headerAlign: 'Left',
      headerClassName: 'theme--header',
      renderCell: (params) => <div>{params.row.departureDate ? dateFormatChange(params.row.departureDate) : ''}</div>,
    },
    {
      field: 'arrivalDateTime',
      headerName: 'Arrival Date',
      flex: 1,
      editable: true,
      align: 'left',
      headerAlign: 'Left',
      headerClassName: 'theme--header',
      renderCell: (params) => (
        <div>{params.row.arrivalDateTime ? dateFormatChange(params.row.arrivalDateTime) : ''}</div>
      ),
    },
    {
      field: 'EORI',
      headerName: 'EORI',
      flex: 1,
      editable: true,
      headerClassName: 'theme--header',
    },
    ...(isOps
      ? [
          {
            field: 'company',
            headerName: 'Company',
            flex: 1,
            editable: true,
            headerClassName: 'theme--header',
          },
        ]
      : []),
    {
      field: 'totalConsignments',
      headerName: '# of Consignments',
      flex: 1,
      editable: true,
      align: 'center',
      headerAlign: 'center',
      headerClassName: 'theme--header',
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      editable: true,
      headerClassName: 'theme--header',
      headerAlign: 'center',
      textAlign: 'center',
      renderCell: (params) => {
        if (params.value.toUpperCase() === 'ERRORS') {
          return (
            <Box
              sx={{
                backgroundColor: '#FFB4B0',
                width: '100%',
                height: '100%',
                textAlign: 'center',
                fontWeight: 'bold',
              }}
            >
              {params.value}
            </Box>
          );
        }
        if (params.value.toUpperCase() === 'ACCEPTED') {
          return (
            <Box
              sx={{
                backgroundColor: '#AEDD94',
                width: '100%',
                height: '100%',
                textAlign: 'center',
                fontWeight: 'bold',
              }}
            >
              {params.value}
            </Box>
          );
        }
        return (
          <Box
            sx={{
              backgroundColor: '#fff',
              width: '100%',
              height: '100%',
              textAlign: 'center',
              fontWeight: 'bold',
            }}
          >
            {params.value}
          </Box>
        );
      },
    },
    {
      field: '23',
      headerName: '',
      width: 50,
      editable: false,
      headerClassName: 'theme--header',
      renderCell: (params) => (
        <div onClick={() => navigatetoShipmentView(params.row)}>
          <ExitToAppIcon className="svg-icons" style={{ color: '#1464F6' }} />
        </div>
      ),
    },
  ];

  return (
    <>
      <SnackbarManager refreshCallback={fetchStatusAndData} />
      <Paper sx={{ width: '100%' }}>
        {loading ? (
          <LoadingSpinner />
        ) : (
          <DataGridPro
            pagination
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            rowCount={store.shipmentTotalCount[0]}
            rows={shipmentData}
            columns={columns}
            getRowId={(row) => {
              if (!row?.headerId) {
                console.error('Missing headerId in row:', row);
              }
              return row?.headerId ?? Math.random();
            }}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 50, page: 0 },
              },
            }}
            pageSizeOptions={[20, 50, 100]}
            sx={{ border: 0 }}
            onRowClick={handleClick}
            getRowClassName={(params) => {
              return params.row.status.toUpperCase() === 'ERRORS' ? 'highlight' : 'non-highlight';
            }}
            disableColumnFilter
          />
        )}
      </Paper>
    </>
  );
}
AcceptedShipment.propTypes = {
  countForApi: PropTypes.string,
  isResponseCame: PropTypes.func,
  aPICall: PropTypes.bool,
  callAPIcallTimeout: PropTypes.bool,
  paginationResetKey: PropTypes.number,
};
